import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { toast } from 'react-toastify';
import * as userMessageService from '../services/userMessageService';
import socketService from '../services/socketService';

interface UserMessage {
  _id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  isRead: boolean;
  createdAt: string;
  priority: 'low' | 'medium' | 'high';
  actionUrl?: string;
  category?: string;
}

const Messages: React.FC = () => {
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'info' | 'warning' | 'error' | 'success'>('all');

  // Fetch messages from API and set up real-time updates
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await userMessageService.getUserMessages({
          page: 1,
          limit: 50 // Get more messages at once
        });
        
        if (response.success) {
          setMessages(response.data);
        } else {
          // Fallback to mock data if API fails
          const mockMessages: UserMessage[] = [
            {
              _id: '1',
              title: 'به سیستم خوش آمدید',
              message: 'حساب کاربری شما با موفقیت ایجاد شد. می‌توانید از تمامی امکانات سیستم استفاده کنید.',
              type: 'success',
              isRead: false,
              createdAt: new Date().toISOString(),
              priority: 'medium'
            },
            {
              _id: '2',
              title: 'بروزرسانی سیستم',
              message: 'سیستم در تاریخ ۱۵ آذر ۱۴۰۳ بروزرسانی خواهد شد. لطفاً فایل‌های مهم خود را پشتیبان‌گیری کنید.',
              type: 'warning',
              isRead: true,
              createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
              priority: 'high'
            },
            {
              _id: '3',
              title: 'اطلاعیه مهم',
              message: 'لطفاً اطلاعات پروفایل خود را بروزرسانی کنید تا بتوانید از تمامی خدمات استفاده کنید.',
              type: 'info',
              isRead: false,
              createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
              priority: 'medium'
            },
            {
              _id: '4',
              title: 'خطا در پردازش',
              message: 'درخواست شماره ۱۲۳۴ به دلیل مشکل فنی قابل پردازش نیست. لطفاً با پشتیبانی تماس بگیرید.',
              type: 'error',
              isRead: true,
              createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
              priority: 'high'
            }
          ];
          setMessages(mockMessages);
        }
      } catch (error) {
        // Fallback to mock data if API call fails
        const mockMessages: UserMessage[] = [
          {
            _id: '1',
            title: 'به سیستم خوش آمدید',
            message: 'حساب کاربری شما با موفقیت ایجاد شد. می‌توانید از تمامی امکانات سیستم استفاده کنید.',
            type: 'success',
            isRead: false,
            createdAt: new Date().toISOString(),
            priority: 'medium'
          },
          {
            _id: '2',
            title: 'بروزرسانی سیستم',
            message: 'سیستم در تاریخ ۱۵ آذر ۱۴۰۳ بروزرسانی خواهد شد. لطفاً فایل‌های مهم خود را پشتیبان‌گیری کنید.',
            type: 'warning',
            isRead: true,
            createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            priority: 'high'
          },
          {
            _id: '3',
            title: 'اطلاعیه مهم',
            message: 'لطفاً اطلاعات پروفایل خود را بروزرسانی کنید تا بتوانید از تمامی خدمات استفاده کنید.',
            type: 'info',
            isRead: false,
            createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            priority: 'medium'
          }
        ];
        setMessages(mockMessages);

      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
    
    // Set up socket listener for new messages
    socketService.setOnNewMessage((newMessage) => {
      // Convert MessageData to UserMessage format and add to the beginning of the list
      const userMessage: UserMessage = {
        _id: newMessage.id,
        title: newMessage.title,
        message: newMessage.message,
        type: newMessage.type,
        isRead: newMessage.isRead,
        createdAt: newMessage.createdAt,
        priority: newMessage.priority,
        actionUrl: newMessage.actionUrl,
        category: newMessage.category
      };
      setMessages(prev => [userMessage, ...prev]);
    });
    
    // Cleanup socket listeners on unmount
    return () => {
      socketService.setOnNewMessage(() => {});
    };
  }, []);

  const markAsRead = async (messageId: string) => {
    try {
      await userMessageService.markMessageAsRead(messageId);
      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId ? { ...msg, isRead: true } : msg
        )
      );
      
      // Emit socket event for real-time updates
      socketService.markMessageAsRead(messageId);
      
      toast.success('پیام به عنوان خوانده شده علامت‌گذاری شد');
    } catch (error) {
      // Fallback to local update if API fails
      setMessages(prev => 
        prev.map(msg => 
          msg._id === messageId ? { ...msg, isRead: true } : msg
        )
      );

    }
  };

  const markAllAsRead = async () => {
    if (markingAllAsRead) return; // Prevent double clicks
    
    setMarkingAllAsRead(true);
    
    try {
      const response = await userMessageService.markAllMessagesAsRead();
      
      // Update local state
      setMessages(prev => 
        prev.map(msg => ({ ...msg, isRead: true }))
      );
      
      // Show success message
      if (response.modifiedCount && response.modifiedCount > 0) {
        toast.success(`${response.modifiedCount} پیام به عنوان خوانده شده علامت‌گذاری شد`);
      } else {
        toast.success('تمامی پیام‌ها به عنوان خوانده شده علامت‌گذاری شدند');
      }
      
      // Refresh messages from server after a short delay to ensure consistency
      setTimeout(async () => {
        try {
          const refreshResponse = await userMessageService.getUserMessages({
            page: 1,
            limit: 50
          });
          if (refreshResponse.success) {
            setMessages(refreshResponse.data);
          }
        } catch (error) {

        }
      }, 500);
      
    } catch (error) {

      toast.error('خطا در علامت‌گذاری پیام‌ها');
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      await userMessageService.deleteMessage(messageId);
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      toast.success('پیام حذف شد');
    } catch (error) {
      // Fallback to local delete if API fails
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      toast.success('پیام حذف شد');

    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesReadFilter = filter === 'all' || 
      (filter === 'read' && msg.isRead) || 
      (filter === 'unread' && !msg.isRead);
    
    const matchesTypeFilter = typeFilter === 'all' || msg.type === typeFilter;
    
    return matchesReadFilter && matchesTypeFilter;
  });

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
        );
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">فوری</span>;
      case 'medium':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">متوسط</span>;
      case 'low':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">عادی</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const unreadCount = messages.filter(msg => !msg.isRead).length;

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">پیام‌ها و اطلاعیه‌ها</h1>
              <p className="text-gray-600 mt-1">
                {unreadCount > 0 ? `${unreadCount} پیام خوانده نشده` : 'همه پیام‌ها خوانده شده‌اند'}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={markingAllAsRead}
              >
                {markingAllAsRead ? 'در حال پردازش...' : 'علامت‌گذاری همه به عنوان خوانده شده'}
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">وضعیت:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">همه</option>
                <option value="unread">خوانده نشده</option>
                <option value="read">خوانده شده</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">نوع:</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">همه</option>
                <option value="info">اطلاعیه</option>
                <option value="success">موفقیت</option>
                <option value="warning">هشدار</option>
                <option value="error">خطا</option>
              </select>
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredMessages.length > 0 ? (
            filteredMessages.map((message) => (
              <div
                key={message._id}
                className={`bg-white rounded-lg shadow-sm border transition-all hover:shadow-md ${
                  message.isRead ? 'border-gray-200' : 'border-blue-200 bg-blue-50'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {getMessageIcon(message.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className={`text-lg font-semibold ${message.isRead ? 'text-gray-900' : 'text-blue-900'}`}>
                            {message.title}
                          </h3>
                          {!message.isRead && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {getPriorityBadge(message.priority)}
                          <span className="text-sm text-gray-500">
                            {formatDate(message.createdAt)}
                          </span>
                        </div>
                      </div>
                      <div className="text-gray-700 mb-3 leading-relaxed">
                        {message.message.split('\n').map((line, index) => (
                          <div key={index} className="mb-1">
                            {line.trim().startsWith('•') ? (
                              <div className="flex items-start">
                                <span className="text-blue-600 ml-2">•</span>
                                <span>{line.replace(/^•\s*/, '')}</span>
                              </div>
                            ) : (
                              <div>{line}</div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {message.actionUrl && (
                          <a
                            href={message.actionUrl}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium bg-blue-50 px-3 py-1 rounded-lg"
                          >
                            مشاهده جزئیات
                          </a>
                        )}
                        {!message.isRead && (
                          <button
                            onClick={() => markAsRead(message._id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            علامت‌گذاری به عنوان خوانده شده
                          </button>
                        )}
                        <button
                          onClick={() => deleteMessage(message._id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          حذف
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-4m-4 0h-4m-4 0H4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">پیامی یافت نشد</h3>
              <p className="text-gray-500">
                {filter === 'unread' ? 'پیام خوانده‌نشده‌ای وجود ندارد' : 'هیچ پیامی برای نمایش وجود ندارد'}
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Messages; 